import { UserApplicationFilter } from '@/pages/UserApplications/components/UserApplicationFilter/UserApplicationFilter';
import { UserApplicationList } from '@/pages/UserApplications/components/UserApplicationList/UserApplicationList';
import { UserApplicationSearch } from '@/pages/UserApplications/components/UserApplicationSearch/UserApplicationSearch';
import { useUserApplicationList } from '@/queries/applications';
import { useEffect, useMemo, useState } from 'react';

export const UserApplicationsPage: React.FC = () => {
  const { data: groups, isLoading } = useUserApplicationList();
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 250);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const filteredGroups = useMemo(() => {
    if (!groups) return [];
    const filteredByGroup = groups.filter(
      (group) => selectedFilters.length === 0 || selectedFilters.includes(group.id),
    );
    if (!debouncedSearch.trim()) {
      return filteredByGroup;
    }
    const searchLower = debouncedSearch.toLowerCase().trim();

    return filteredByGroup
      .map((group) => {
        const filteredApps = group.applications.filter((app) => {
          if (app.title.toLowerCase().includes(searchLower)) {
            return true;
          }

          if (app.description && app.description.toLowerCase().includes(searchLower)) {
            return true;
          }

          return false;
        });

        return { ...group, applications: filteredApps };
      })
      .filter((group) => group.applications.length > 0);
  }, [groups, debouncedSearch, selectedFilters]);

  const handleToggleFilter = (groupId: string) => {
    setSelectedFilters((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId],
    );
  };

  const handleChangeSearch = (text: string) => {
    setSearchValue(text);
  };

  const handleResetFilters = () => {
    setSelectedFilters([]);
  };

  if (isLoading) {
    return <div className="px-3 py-8 text-center">Загрузка приложений...</div>;
  }

  return (
    <div className="bg-white shadow-lg pb-32 flex w-full min-w-0 max-w-full flex-col">
      <div className="flex w-full min-w-0 max-w-full flex-col items-start border-b border-[#f4f4f5] lg:flex-row">
        <UserApplicationSearch onChange={handleChangeSearch} value={searchValue} />
        <UserApplicationFilter
          selectedFilters={selectedFilters}
          onToggleFilter={handleToggleFilter}
          groups={groups}
          onClearFilter={handleResetFilters}
          className="min-w-0 w-full lg:w-max lg:max-w-[60%] lg:flex-none"
        />
      </div>
      {filteredGroups.length ? (
        <UserApplicationList groups={filteredGroups} />
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-2xl">Приложений не найдено</div>
        </div>
      )}
    </div>
  );
};
